// hooks/useSettings.js
import { useState, useEffect } from 'react';
import apiClient from '../api/client';

export const useEmailSenders = () => {
  const [emailSenders, setEmailSenders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch email senders on component mount
  useEffect(() => {
    fetchEmailSenders();
  }, []);

  const fetchEmailSenders = async () => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      const response = await apiClient.get('/email-senders', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmailSenders(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching email senders:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const addSender = async (senderData) => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      const response = await apiClient.post('/email-senders', senderData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmailSenders([...emailSenders, response.data]);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error adding email sender:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const updateSender = async (id, senderData) => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      const response = await apiClient.put(`/email-senders/${id}`, senderData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmailSenders(emailSenders.map(sender => 
        sender.id === id ? response.data : sender
      ));
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error updating email sender:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSender = async (id) => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      await apiClient.delete(`/email-senders/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmailSenders(emailSenders.filter(sender => sender.id !== id));
      setError(null);
      return true;
    } catch (err) {
      console.error('Error deleting email sender:', err);
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { emailSenders, isLoading, error, addSender, updateSender, deleteSender, refreshSenders: fetchEmailSenders };
};

export const useVerifiedDomains = () => {
  const [domains, setDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch verified domains on component mount
  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      const response = await apiClient.get('/email-senders/domains', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDomains(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching verified domains:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const addDomain = async (domain) => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      const response = await apiClient.post('/email-senders/domains/verify', { domain }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // If successful, add to domains list in pending state
      if (response.data.status === 'pending_verification') {
        const newDomain = {
          id: response.data.domain_id,
          domain,
          is_verified: false,
          dns_records: response.data.dns_records
        };
        setDomains([...domains, newDomain]);
      }
      
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error adding domain:', err);
      setError(err.message);
      return { status: 'error', message: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  const checkVerification = async (domainId) => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      const response = await apiClient.post(`/email-senders/domains/${domainId}/check`, null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Update domain verification status
      if (response.data.status === 'verified') {
        setDomains(domains.map(domain => 
          domain.id === domainId ? { ...domain, is_verified: true } : domain
        ));
      }
      
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error checking domain verification:', err);
      setError(err.message);
      return { status: 'error', message: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Add the createSenderWithDomain function inside the hook
  const createSenderWithDomain = async (senderData, domainId) => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      // Get the domain info from the verified domains
      const domainToUse = domains.find(d => d.id === domainId);
      
      if (!domainToUse || !domainToUse.is_verified) {
        throw new Error("Domain is not verified or does not exist");
      }
      
      // Extract domain from email or use the verified domain
      const emailParts = senderData.email.split('@');
      const username = emailParts[0];
      const domainName = domainToUse.domain;
      
      // Create sender with the domain-specific email
      const response = await apiClient.post('/email-senders', {
        display_name: senderData.display_name,
        email: `${username}@${domainName}`,
        domain_id: domainId,
        smtp_host: "smtp.sendgrid.net", // Default for SendGrid
        smtp_port: 587,
        smtp_username: "apikey",
        smtp_password: ""
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Refresh the domains list
      await fetchDomains();
      
      return response.data;
    } catch (err) {
      console.error('Error adding email sender with domain:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { 
    domains, 
    isLoading, 
    error, 
    addDomain, 
    checkVerification, 
    createSenderWithDomain,
    refreshDomains: fetchDomains 
  };
};